import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator } from "lucide-react";
import { trackCalcUse } from "@/lib/analytics";

const MonthlyCalculatorSection = () => {
  const [amount, setAmount] = useState("");
  const [months, setMonths] = useState("12");
  const [includeFee, setIncludeFee] = useState(false);

  const calculateMonthly = () => {
    const amountNum = parseFloat(amount);
    const monthsNum = parseInt(months);
    
    if (!amountNum || !monthsNum || amountNum <= 0) return { principal: 0, withFee: 0 };
    
    const principal = Math.round(amountNum / monthsNum);
    const withFee = Math.round((amountNum * 1.015) / monthsNum); // 1.5% example fee
    
    return { principal, withFee };
  };

  const monthly = calculateMonthly();

  // Track calculator usage when amount and months change
  useEffect(() => {
    const amountNum = parseFloat(amount);
    const monthsNum = parseInt(months);
    
    if (amountNum > 0 && monthsNum > 0) {
      // Debounce tracking to avoid too many events
      const timeoutId = setTimeout(() => {
        const monthlyPayment = includeFee ? monthly.withFee : monthly.principal;
        trackCalcUse(amountNum, monthsNum, monthlyPayment);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [amount, months, monthly.principal, monthly.withFee, includeFee]);

  return (
    <section className="py-20 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-card rounded-2xl p-8 card-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 gradient-bg rounded-lg flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold">Monthly Payment Calculator</h2>
              <p className="text-sm text-muted-foreground">Get an estimate of your monthly payments</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (AED)</Label>
              <Input
                id="amount"
                type="number"
                placeholder="25,000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-12 text-base"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="months">Plan Length (months)</Label>
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger className="h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="24">24 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Fee Toggle */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeFee}
                onChange={(e) => setIncludeFee(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-body">Include example partner fee 1.5%</span>
            </label>
          </div>

          {amount && (
            <div className="bg-gradient-to-r from-flexaura-teal/10 to-flexaura-purple/10 rounded-lg p-6 text-center">
              <div className="text-3xl font-heading font-bold gradient-text mb-2">
                AED {(includeFee ? monthly.withFee : monthly.principal).toLocaleString()} / month
              </div>
              <p className="text-sm text-muted-foreground">
                {includeFee ? "Total cost vs principal" : "Estimates only"} - final terms via provider
              </p>
            </div>
          )}
          
          <div className="mt-6 p-4 bg-flexaura-gray/50 rounded-lg">
            <p className="text-xs text-muted-foreground text-center">
              This calculator provides estimates only. Actual rates and terms will vary by provider, 
              your credit profile, and purchase details. Final terms will be shown during checkout.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MonthlyCalculatorSection;